import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { ButtonText } from 'src/components/ButtonText';
import { ChevronIcon } from 'src/components/ChevronIcon';

import { useRootWidth } from './useRootWidth';
import { useStyles } from './useStyles';
import {
  calculateItemsProps,
  calculatePanelHeight,
  DEFAULT_ROWS_COUNT,
  prepareMoreTagsTooltip,
} from './utils';

const onlyValidElements = (items) => items.filter(({ element }) => element);

export const TagsPositioner = memo(
  ({
    className,
    classes,
    items,
    maxRows,
    tagsGap,
    isExpandable,
    children,
    moreButtonNewLine,
    moreLabelAppendix,
    moreLabelMaxWidth,
    horizontalTagsGap,
    selectedTags,
    onExpandShowed,
    setShowMoreButton,
    showMoreChip,
    isMoreChipExpanded,
    onMoreChipClick,
    moreChipRef,
  }) => {
    const styles = useStyles({ classes });

    const [rootElement, setRootElement] = useState(null);
    const refElementsInfo = useRef([]);
    const [rowHeight, setRowHeight] = useState(0);
    const [itemsStyles, setItemsStyles] = useState([]);
    const [firstHiddenItemIndex, setFirstHiddenItemIndex] = useState(0);
    const [rowsCount, setRowsCount] = useState(DEFAULT_ROWS_COUNT);
    const [moreCount, setMoreCount] = useState(0);
    const [moreStyles, setMoreStyles] = useState({});
    const [isExpanded, setIsExpanded] = useState(false);

    const rootWidth = useRootWidth(rootElement);

    useEffect(() => {
      if (onExpandShowed) {
        onExpandShowed(moreCount > 0 && isExpandable);
      }
    }, [isExpandable, moreCount, onExpandShowed]);

    const storeItemRef = (index, element) => {
      const elementsInfo = refElementsInfo.current;

      elementsInfo[index] = { element };
      if (element) {
        elementsInfo[index].height = element.offsetHeight;
        elementsInfo[index].width = element.offsetWidth;
        setRowHeight((prev) => (element.offsetHeight > prev ? element.offsetHeight : prev));
      }
    };
    const options = useMemo(
      () => ({
        maxRows,
        tagsGap,
        horizontalTagsGap,
        rootWidth,
        rowHeight,
        isExpanded,
        isExpandable,
        rowsCount,
        moreLabelMaxWidth,
        showMoreChip,
      }),
      [
        maxRows,
        tagsGap,
        horizontalTagsGap,
        rootWidth,
        rowHeight,
        isExpanded,
        rowsCount,
        isExpandable,
        moreLabelMaxWidth,
        showMoreChip,
      ]
    );

    useEffect(() => {
      // This "effect" gets triggered after component is rendered and we have all "pain point" refs
      // we just need to cut "empty" elements, in case number of "pains" dropped
      const elementsInfo = onlyValidElements(refElementsInfo.current);

      const newProps = calculateItemsProps(
        elementsInfo,
        options,
        moreButtonNewLine,
        setShowMoreButton
      );

      setItemsStyles(newProps.itemsStyles);
      setRowsCount(newProps.rowsCount);
      setMoreCount(newProps.moreCount);
      setMoreStyles(newProps.moreStyles);
      setFirstHiddenItemIndex(newProps.firstHiddenItemIndex);
      // note: selectedTags.length is required to recalculate position once a tag has additional check icon in it, should push the rest in the same line
    }, [items, moreButtonNewLine, options, selectedTags.length, setShowMoreButton]);

    useEffect(() => {
      if (rowsCount <= maxRows) {
        setIsExpanded(false);
      }
    }, [maxRows, rowsCount]);

    const moreTagsTooltip = useMemo(
      () => prepareMoreTagsTooltip(items, itemsStyles),
      [items, itemsStyles]
    );

    if (!children) {
      return null;
    }

    return (
      <div
        className={cx(styles.root, className)}
        ref={setRootElement}
        style={{ height: calculatePanelHeight(rowsCount, options) }}
      >
        {items.map((item, index) =>
          children({
            item,
            key: item.id || item.text || index,
            ref: (ref) => storeItemRef(index, ref),
            style: itemsStyles[index],
          })
        )}

        {moreCount > 0 && showMoreChip && (
          <div
            className={styles.showMoreChip}
            onClick={() => onMoreChipClick(items?.slice(firstHiddenItemIndex))}
          >
            {'More '}
            <ChevronIcon
              className={styles.icon}
              direction={isMoreChipExpanded ? 'up' : 'down'}
            />
          </div>
        )}

        {moreCount > 0 && !showMoreChip && !isExpandable && (
          <Tooltip title={moreTagsTooltip}>
            <span
              className={styles.labelMore}
              style={moreStyles}
            >
              +{moreCount} {moreLabelAppendix}
            </span>
          </Tooltip>
        )}

        {moreCount > 0 && !showMoreChip && isExpandable && !isExpanded && (
          <ButtonText
            className={styles.showMoreLessButton}
            style={moreStyles}
            onClick={() => {
              setIsExpanded(true);
            }}
          >
            Show {moreCount} More
          </ButtonText>
        )}

        {isExpanded && (
          <ButtonText
            className={styles.showMoreLessButton}
            style={moreStyles}
            onClick={() => {
              setIsExpanded(false);
            }}
          >
            Show Less
          </ButtonText>
        )}
      </div>
    );
  }
);

TagsPositioner.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape(),
  items: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape()])).isRequired,
  maxRows: PropTypes.number,
  tagsGap: PropTypes.number,
  children: PropTypes.func.isRequired,
  isExpandable: PropTypes.bool,
  showMoreChip: PropTypes.bool,
  moreLabelAppendix: PropTypes.string,
  moreLabelMaxWidth: PropTypes.number,
  horizontalTagsGap: PropTypes.number,
  selectedTags: PropTypes.arrayOf(PropTypes.string),
  onExpandShowed: PropTypes.func,
  onMoreChipClick: PropTypes.func,
  moreButtonNewLine: PropTypes.bool,
  setShowMoreButton: PropTypes.func,
  isMoreChipExpanded: PropTypes.bool,
  moreChipRef: PropTypes.func,
};
TagsPositioner.defaultProps = {
  className: '',
  classes: null,
  maxRows: null,
  tagsGap: null,
  isExpandable: false,
  showMoreChip: false,
  moreLabelAppendix: null,
  moreLabelMaxWidth: 0,
  horizontalTagsGap: null,
  selectedTags: [],
  onExpandShowed: null,
  onMoreChipClick: null,
  moreButtonNewLine: false,
  setShowMoreButton: null,
  isMoreChipExpanded: false,
  moreChipRef: null,
};
